<template>
    <div class="availability">
        <SvgSprite />

        <div class="availability-container">
            <div class="availability-container__header">
                <h2>Availability calendar</h2>
                <p>
                    Thank you for using our availability checker, which gives you an indication of the cabins and car deck space that may be available 
                    on future ferry sailings between Aberdeen, Kirkwall and Lerwick. A Pentland Firth availability calendar will be available soon. 
                    On sailings where cabins or car deck space is not available, there may still be space, seats, and pods for passengers.
                </p>
                <br />
                <p>
                    Please note that passengers should use the <a class="link" href="http://www.northlinkferries.co.uk/booking-start/">online booking system</a>
                    to confirm availability and reservations are not confirmed until 
                    the booking has been paid for in full.
                </p>
                <br />
                <p>
                    Please note - cabins shown here on the day of sailing may not be available after the ship has departed from Aberdeen or Lerwick.
                </p>
            </div>
            <div class="availability-container__inputs">
                <CalendarSelections
                    @updateFilters="onFiltersUpdate"
                    :filters="filters"
                    :isLoadingConfig="isLoadingFilters"
                    :isLoadingUpdate="isLoading"
                />
            </div>

            <p v-if="route" class="display-route">{{ route }}</p>
            <div class="availability-container__calendar">
                <Calendar @dateChange="onDateChange" :hasVehicle="!!params.vehicle" :hasCabin="!!params.cabin" :attributes="dates" />

                <div v-if="isLoading" class="date-details__isLoading">
                    <BaseSpinner color="pink" :size="20" />
                </div>
            </div>
            <a href="/booking-start" class="booking-link">
                <VBtn :disable="!dates?.length">Book now</VBtn>
            </a>
        </div>
    </div>
</template>

<script setup lang="ts">
import Calendar from "@/modules/AvailabilityCalendar/components/AvailabilityCalendar.vue";
import CalendarSelections from "@/modules/AvailabilityCalendar/components/CalendarSelections.vue";
import useAvailabilityFiltersQuery from "@/modules/AvailabilityCalendar/composables/useAvailabilityFiltersQuery";
import useCalendarQuery from "@/modules/AvailabilityCalendar/composables/useCalendarQuery";
import { computed } from "vue";
import { monthChangeEvent } from "./models/AvailabilityDate";
import { FilterState } from "./models/AvailabilityFilter";
import { VBtn } from "v-ferry-components";
import SvgSprite from "@/components/base/SvgSprite.vue";
import BaseSpinner from "@/components/base/BaseSpinner.vue";

const { data: filters, isLoading: isLoadingFilters } = useAvailabilityFiltersQuery();

const { dates, isLoading, params, updateParams } = useCalendarQuery();

const route = computed(() => filters.value?.routes.find((r) => r.value === params.value.route)?.label);

const onFiltersUpdate = (filters: FilterState) => {
    updateParams({ ...params.value, ...filters });
};

const onDateChange = (date: monthChangeEvent) => {
    updateParams({ ...params.value, month: date.month, year: date.year });
};
</script>

<style lang="scss" scoped>
.availability {
    display: flex;
    justify-content: center;
    margin: 0 auto;
    padding: 20px;
    max-width: 900px;
    font-family: HelveticaNeue, sans-serif;
    box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.1);
    background-color: white;
    margin-top: 1.875rem;

    @include desktop {
        margin-top: 0.625rem;
    }

    &-container {
        width: 100%;
        max-width: 1800px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;

        &__header {
            width: 100%;
            align-self: flex-start;
            margin-bottom: 40px;
            h2,
            p {
                margin: 0px;
            }

            h2 {
                font-size: 2.25rem;
                color: var(--c-base-blue);
                font-weight: normal;
                text-transform: capitalize;
            }

            p {
                font-size: 0.875rem;
                color: var(--c-grey-100);
            }
        }

        .link {
            color: #0071aa;
            text-decoration: underline;
        }

        &__inputs {
            width: 100%;
            max-width: 1200px;
        }

        .display-route {
            margin: 0;
            font-size: 1.375rem;
            font-weight: bold;
        }

        &__calendar {
            * {
                text-transform: uppercase;
            }
            width: 100%;
            margin-top: 30px;
            position: relative;
        }

        .booking-link {
            text-decoration: none;
            z-index: 9;
        }
    }

    --v-btn-font-size: 0.875rem;
}
</style>
